// OIDC Authorization Code + PKCE against the Cognito hosted UI, hand-rolled.
//
// Public client + PKCE because we live in the browser and can't keep a secret.
//
// Storage layering:
//   - Tokens (id_token, access_token) live in localStorage by default, so a
//     session is shared by tabs on the same origin and survives restarts. A
//     host can supply a different storage implementation when needed.
//   - The PKCE verifier+state, in contrast, live in localStorage. We
//     observed that real-world flows (8+ open tabs to the same origin,
//     Chrome session restore, anti-tracking heuristics, redirect lands
//     in a different tab than the one that initiated login) routinely
//     cross sessionStorage boundaries between `login()` and
//     `handleCallback()`. Keeping PKCE in localStorage makes the
//     callback succeed regardless of which tab lands on it. The
//     localStorage entry is consumed (deleted) on first successful or
//     failed callback, so it doesn't accumulate.
//
// Both keys include the clientId so, in the same tab, two different OIDC
// clients (e.g. platform-spa and platform-server) don't collide — useful
// if a UI ever holds both simultaneously.

const enc = new TextEncoder();

const b64url = (buf: ArrayBuffer | Uint8Array): string =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replaceAll("=", "");

const sha256 = (input: string | ArrayBuffer): Promise<ArrayBuffer> =>
  crypto.subtle.digest("SHA-256", typeof input === "string" ? enc.encode(input) : input);

function randomString(bytes = 32): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return b64url(a);
}

/** Claims we surface from a Cognito ID token. Open to extension. */
export interface JwtClaims {
  sub: string;
  preferred_username?: string;
  email?: string;
  /** Custom claim mapped in the realm — names the MinIO policy. */
  policy?: string;
  [key: string]: unknown;
}

/** Token bundle returned by the Cognito token endpoint. */
export interface OidcSession {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  scope?: string;
}

export interface OidcClientConfig {
  /** Cognito hosted-UI domain — e.g.
   *  `https://my-pool.auth.eu-central-1.amazoncognito.com`. The pool's
   *  hosted UI must be enabled and a domain prefix configured;
   *  `s3p init-project` does that during provisioning. */
  domain: string;
  /** Cognito App Client ID. */
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  /** Where session tokens are kept. Defaults to browser-wide localStorage. */
  tokenStorage?: Storage;
}

export interface OidcClient {
  /** Hosted-UI redirect. `identityProvider` jumps straight into a federated
   *  IdP (e.g. "Google") via `identity_provider=` on /oauth2/authorize. */
  login(opts?: { identityProvider?: string }): Promise<void>;
  logout(): void;
  handleCallback(): Promise<OidcSession | null>;
  getSession(): OidcSession | null;
  /** Use the cached refresh_token to mint a new id_token + access_token.
   *  Updates session storage and returns the new session, or null if no
   *  refresh_token is available / the refresh itself failed (caller
   *  must redirect to login in that case). Used by the SDK's STS
   *  rotation path so the long-running tab survives past the OIDC
   *  id-token lifespan (Cognito default: 1 h). */
  refreshSession(): Promise<OidcSession | null>;
  /** Adopt tokens minted OUTSIDE the redirect flow (native app.auth) into
   *  the same storage the redirect flow uses, so everything downstream is
   *  flow-agnostic. */
  setSession(tokens: OidcSession): void;
  /** Local-only sign-out: drop the stored session without the hosted-UI
   *  logout redirect (native sessions never made a hosted-UI cookie). */
  clearSession(): void;
}

export function parseJwt(token: string): JwtClaims {
  const parts = token.split(".");
  const payload = parts[1];
  if (!payload) throw new Error("malformed JWT: missing payload segment");
  return JSON.parse(atob(payload.replaceAll("-", "+").replaceAll("_", "/"))) as JwtClaims;
}

/** The `exp` claim of a JWT, in epoch-milliseconds (the claim itself is
 *  epoch-seconds). Returns null when the token has no `exp`, can't be
 *  parsed, or carries a non-numeric `exp`. Used by the SDK to refresh an
 *  id_token *before* it dies rather than after AWS rejects it. */
export function jwtExpiry(token: string): number | null {
  let claims: JwtClaims & { exp?: unknown };
  try {
    claims = parseJwt(token);
  } catch {
    return null;
  }
  return typeof claims.exp === "number" ? claims.exp * 1000 : null;
}

/** True when `token` is past its `exp` (or within `skewMs` of it). A token
 *  with no readable `exp` is treated as expired — we'd rather refresh a
 *  token we can't reason about than send it and get a raw AWS 400. */
export function jwtExpired(token: string, skewMs = 0): boolean {
  const exp = jwtExpiry(token);
  if (exp === null) return true;
  return exp - Date.now() < skewMs;
}

/** WHERE a client's session tokens live (browser-wide localStorage, per the
 *  header — NOT per-tab sessionStorage, which this said until the two drifted).
 *  Note localStorage is partitioned by ORIGIN: a session written on one host is
 *  invisible to another, which is why every s3p auth page lives on one host.
 *  Exported so
 *  out-of-band sign-in flows (a funnel page calling `createAuth` directly,
 *  before any PlatformClient exists) can persist a session THE SDK's OIDC
 *  client will adopt — instead of hardcoding this key's shape and silently
 *  breaking when it changes. */
export function persistOidcSession(
  clientId: string,
  session: OidcSession,
  tokenStorage: Storage = localStorage,
): void {
  tokenStorage.setItem(`platform.oidc.${clientId}`, JSON.stringify(session));
}

export function createOidcClient(config: OidcClientConfig): OidcClient {
  const { domain, clientId, redirectUri, postLogoutRedirectUri, tokenStorage = localStorage } = config;
  // Cognito hosted UI endpoints. Same OAuth + PKCE protocol as Keycloak;
  // different URL pattern. The token endpoint is at `/oauth2/token`,
  // authorize at `/oauth2/authorize`, logout at `/logout` with the
  // param `logout_uri` (NOT `post_logout_redirect_uri` — Cognito uses
  // its own pre-OAuth-3.0 logout shape).
  const issuer = domain.replace(/\/$/, "");
  const sessionKey = `platform.oidc.${clientId}`;
  const pkceKey = `platform.pkce.${clientId}`;

  async function login(opts: { identityProvider?: string } = {}): Promise<void> {
    const verifier = randomString(48);
    const challenge = b64url(await sha256(verifier));
    const state = randomString(16);
    localStorage.setItem(pkceKey, JSON.stringify({ verifier, state }));

    const url = new URL(`${issuer}/oauth2/authorize`);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("client_id", clientId);
    url.searchParams.set("redirect_uri", redirectUri);
    url.searchParams.set("scope", "openid profile email");
    url.searchParams.set("state", state);
    url.searchParams.set("code_challenge", challenge);
    url.searchParams.set("code_challenge_method", "S256");
    // Third-party sign-in (Google/…): hosted UI skips its own form and goes
    // straight to the provider. The ONE flow Cognito only offers via
    // /oauth2/authorize — exactly what this dormant path was kept for.
    if (opts.identityProvider) url.searchParams.set("identity_provider", opts.identityProvider);
    window.location.assign(url.toString());
  }

  async function handleCallback(): Promise<OidcSession | null> {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (!code) return null;

    // Strip the URL of OIDC callback params NO MATTER what happens
    // below — otherwise a failed exchange + page refresh re-triggers
    // the same broken callback in a loop. The PKCE entry, likewise,
    // shouldn't outlive this attempt: success → it's consumed; failure
    // → it's stale and must not bleed into the next login().
    function cleanUpCallback(): void {
      localStorage.removeItem(pkceKey);
      window.history.replaceState({}, "", window.location.pathname);
    }

    const raw = localStorage.getItem(pkceKey);
    const stored = raw ? (JSON.parse(raw) as { verifier: string; state: string }) : null;
    if (!stored || stored.state !== state) {
      cleanUpCallback();
      throw new Error(
        "OIDC state mismatch (possible CSRF, stale tab, or login initiated in a different tab — " +
          "the URL has been cleaned, click Sign in to retry).",
      );
    }

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: redirectUri,
      client_id: clientId,
      code_verifier: stored.verifier,
    });
    let res: Response;
    try {
      res = await fetch(`${issuer}/oauth2/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
    } catch (e) {
      cleanUpCallback();
      throw e;
    }
    if (!res.ok) {
      const errText = await res.text();
      cleanUpCallback();
      throw new Error(`token endpoint: ${res.status} ${errText}`);
    }
    const tokens = (await res.json()) as OidcSession;
    tokenStorage.setItem(sessionKey, JSON.stringify(tokens));
    cleanUpCallback();
    return tokens;
  }

  function getSession(): OidcSession | null {
    const raw = tokenStorage.getItem(sessionKey);
    return raw ? (JSON.parse(raw) as OidcSession) : null;
  }

  async function refreshSession(): Promise<OidcSession | null> {
    const current = getSession();
    if (!current?.refresh_token) return null;
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: current.refresh_token,
      client_id: clientId,
    });
    let res: Response;
    try {
      res = await fetch(`${issuer}/oauth2/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
    } catch {
      return null;
    }
    if (!res.ok) {
      // Refresh token rejected (expired or revoked). Drop the session so
      // subsequent ops fail fast with "not authenticated" instead of
      // looping on a dead token.
      tokenStorage.removeItem(sessionKey);
      return null;
    }
    const tokens = (await res.json()) as OidcSession;
    // Cognito's refresh grant does NOT rotate the refresh token — the response
    // omits it. Carry the one we hold forward, or the next refresh cycle finds
    // no refresh_token and the session dies an hour in.
    const next: OidcSession = {
      ...tokens,
      refresh_token: tokens.refresh_token ?? current.refresh_token,
    };
    tokenStorage.setItem(sessionKey, JSON.stringify(next));
    return next;
  }

  function logout(): void {
    tokenStorage.removeItem(sessionKey);
    // Cognito's logout endpoint uses `logout_uri` (NOT
    // `post_logout_redirect_uri`) and only accepts URIs registered
    // against the app client's `LogoutURLs`. No `id_token_hint` (it's
    // unsupported and ignored).
    const url = new URL(`${issuer}/logout`);
    url.searchParams.set("client_id", clientId);
    url.searchParams.set("logout_uri", postLogoutRedirectUri);
    window.location.assign(url.toString());
  }

  function setSession(tokens: OidcSession): void {
    tokenStorage.setItem(sessionKey, JSON.stringify(tokens));
  }
  function clearSession(): void {
    tokenStorage.removeItem(sessionKey);
  }

  return { login, logout, handleCallback, getSession, refreshSession, setSession, clearSession };
}
